/**
 * Species per structure on a timber RFQ invite.
 *
 * A supplier's invite lists the species they are asked to price. By default every
 * species applies to every structure, which duplicates each structure's list once
 * per species in the CSV / PDF. `structureSpecies` narrows that: structure id → the
 * species to price on that structure only. Pure helpers shared by the exports, the
 * portal, the wizard and the scope-drift check. Mirrored in
 * `functions/src/rfqPublishTimberCsv.ts`.
 */
import type { ProposalNodeSet, RfqPackage, TimberSupplierInvite, TimberSupplierStructureRef } from '../types';
import { normalizeName } from './timberRfqStructureScope';

/** One structure's species rule, resolved with its name so hub-CSV node sets (regenerated ids) still match. */
export type StructureSpeciesRule = {
  id: string;
  name: string;
  species: string[];
};

type InviteSpeciesFields = Pick<TimberSupplierInvite, 'requestedSpecies' | 'structures' | 'structureSpecies'>;

function cleanSpecies(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  for (const raw of list) {
    const sp = typeof raw === 'string' ? raw.trim() : '';
    if (sp && !out.some(x => x.toLowerCase() === sp.toLowerCase())) out.push(sp);
  }
  return out;
}

/**
 * Rules from an invite's `structureSpecies`, with names looked up from the invite's
 * structure refs (or `structureNames` when the invite is unscoped). A structure whose
 * list equals every requested species carries no rule — nothing to narrow.
 */
export function structureSpeciesRulesFromInvite(
  invite: InviteSpeciesFields | undefined,
  structureNames: TimberSupplierStructureRef[] = [],
): StructureSpeciesRule[] {
  const map = invite?.structureSpecies;
  if (!map || typeof map !== 'object') return [];
  const requested = cleanSpecies(invite?.requestedSpecies);
  const nameOf = new Map<string, string>();
  for (const ref of [...(invite?.structures ?? []), ...structureNames]) {
    if (ref?.id && ref.name && !nameOf.has(ref.id)) nameOf.set(ref.id, ref.name);
  }
  const rules: StructureSpeciesRule[] = [];
  for (const [id, raw] of Object.entries(map)) {
    if (!id) continue;
    const species = cleanSpecies(raw).filter(sp => !requested.length || requested.some(r => r.toLowerCase() === sp.toLowerCase()));
    if (requested.length && species.length === requested.length) continue;
    rules.push({ id, name: nameOf.get(id) ?? '', species });
  }
  return rules;
}

/** Same shape built from a package's snapshot (`structureSpecies` + `scopeStructures` / node-set names). */
export function structureSpeciesRulesFromPackage(
  pkg: Pick<RfqPackage, 'structureSpecies' | 'scopeStructures' | 'proposalSnapshot'> | undefined,
  requestedSpecies: string[] = [],
): StructureSpeciesRule[] {
  const names: TimberSupplierStructureRef[] = [
    ...(pkg?.scopeStructures ?? []),
    ...((pkg?.proposalSnapshot?.nodeSets ?? []).map(ns => ({ id: ns.id, name: ns.name })) as TimberSupplierStructureRef[]),
  ];
  return structureSpeciesRulesFromInvite({ requestedSpecies, structureSpecies: pkg?.structureSpecies }, names);
}

/** The rule for a node set, matched by id first and then by normalized name. */
export function structureSpeciesRuleFor(
  ns: Pick<ProposalNodeSet, 'id' | 'name'> | TimberSupplierStructureRef,
  rules: StructureSpeciesRule[] | undefined,
): StructureSpeciesRule | undefined {
  if (!rules?.length) return undefined;
  const byId = rules.find(r => r.id && r.id === ns.id);
  if (byId) return byId;
  const key = normalizeName(ns.name);
  return key ? rules.find(r => normalizeName(r.name) === key) : undefined;
}

/**
 * Requested species entries that apply to one structure: the invite's list filtered
 * to that structure's rule, or the full list when no rule exists. An explicit empty
 * rule means "nothing to price here" and yields [].
 */
export function requestedEntriesForStructure<T extends { species: string }>(
  ns: Pick<ProposalNodeSet, 'id' | 'name'>,
  requestedEntries: T[],
  rules: StructureSpeciesRule[] | undefined,
): T[] {
  const rule = structureSpeciesRuleFor(ns, rules);
  if (!rule) return requestedEntries;
  const allowed = new Set(rule.species.map(sp => sp.toLowerCase()));
  return requestedEntries.filter(e => allowed.has(e.species.trim().toLowerCase()));
}

/** Species names this structure is asked for (display) — rule species, else every requested species. */
export function speciesForStructure(
  ns: Pick<ProposalNodeSet, 'id' | 'name'> | TimberSupplierStructureRef,
  requestedSpecies: string[],
  rules: StructureSpeciesRule[] | undefined,
): string[] {
  const rule = structureSpeciesRuleFor(ns, rules);
  return rule ? rule.species : requestedSpecies;
}

/**
 * Normalized `structureSpecies` map: drops empty ids, dedupes species, removes
 * species outside `requestedSpecies`, and drops entries equal to the full list.
 * Returns undefined when nothing narrows anything.
 */
export function normalizeStructureSpecies(
  map: Record<string, string[]> | undefined,
  requestedSpecies: string[],
): Record<string, string[]> | undefined {
  if (!map) return undefined;
  const requested = cleanSpecies(requestedSpecies);
  const out: Record<string, string[]> = {};
  for (const [id, raw] of Object.entries(map)) {
    if (!id) continue;
    const species = cleanSpecies(raw).filter(sp => !requested.length || requested.some(r => r.toLowerCase() === sp.toLowerCase()));
    if (requested.length && species.length === requested.length) continue;
    out[id] = species;
  }
  return Object.keys(out).length ? out : undefined;
}

/** True when two structure-species maps ask for the same thing (order-insensitive, by id). */
export function structureSpeciesEqual(
  a: Record<string, string[]> | undefined,
  b: Record<string, string[]> | undefined,
  requestedSpecies: string[],
): boolean {
  const na = normalizeStructureSpecies(a, requestedSpecies) ?? {};
  const nb = normalizeStructureSpecies(b, requestedSpecies) ?? {};
  const ka = Object.keys(na).sort();
  const kb = Object.keys(nb).sort();
  if (ka.length !== kb.length || ka.some((k, i) => k !== kb[i])) return false;
  return ka.every(k => {
    const sa = na[k].map(s => s.toLowerCase()).sort();
    const sb = nb[k].map(s => s.toLowerCase()).sort();
    return sa.length === sb.length && sa.every((s, i) => s === sb[i]);
  });
}

/**
 * Human lines describing what changed per structure between two maps, e.g.
 * "Sale Barn — Glulam only (was Glulam, Pine)". Used in the scope-change email.
 */
export function structureSpeciesChangeLines(
  before: Record<string, string[]> | undefined,
  after: Record<string, string[]> | undefined,
  requestedSpecies: string[],
  structures: TimberSupplierStructureRef[],
): string[] {
  const nb = normalizeStructureSpecies(before, requestedSpecies) ?? {};
  const na = normalizeStructureSpecies(after, requestedSpecies) ?? {};
  const ids = Array.from(new Set([...Object.keys(nb), ...Object.keys(na)]));
  const nameOf = (id: string) => structures.find(s => s.id === id)?.name || id;
  const lines: string[] = [];
  for (const id of ids) {
    const was = nb[id] ?? requestedSpecies;
    const now = na[id] ?? requestedSpecies;
    const same = was.length === now.length && was.every(sp => now.some(n => n.toLowerCase() === sp.toLowerCase()));
    if (same) continue;
    const nowText = now.length ? `${now.join(', ')}${now.length === 1 ? ' only' : ''}` : 'no species';
    lines.push(`${nameOf(id)} — ${nowText} (was ${was.length ? was.join(', ') : 'none'})`);
  }
  return lines;
}
